import React, { useEffect, useRef, useState } from 'react';

const NICHES = [
  {
    label: 'Sport',
    left: {
      image: require('../../images/creator.webp'),
      name: 'Nora Well',
      meta: '142k+ followers',
    },
    right: {
      image: require('../../images/pic1.webp'),
      name: 'Jasmine Patel',
      meta: '820k+ followers',
    },
  },
  {
    label: 'Music',
    left: {
      image: require('../../images/premiummessage.webp'),
      name: 'Mia Rose',
      meta: '98k+ followers',
    },
    right: {
      image: require('../../images/groups1.webp'),
      name: 'Jon Perez',
      meta: '240k+ followers',
    },
  },
  {
    label: 'Art',
    left: {
      image: require('../../images/pic1.webp'),
      name: 'Nora Well',
      meta: '142k+ followers',
    },
    right: {
      image: require('../../images/creator.webp'),
      name: 'Jasmine Patel',
      meta: '820k+ followers',
    },
  },
  {
    label: 'Fashion',
    left: {
      image: require('../../images/groups1.webp'),
      name: 'Sophia Cole',
      meta: '191k+ followers',
    },
    right: {
      image: require('../../images/premiummessage.webp'),
      name: 'Elena Mood',
      meta: '430k+ followers',
    },
  },
  {
    label: 'Travel',
    left: {
      image: require('../../images/creator.webp'),
      name: 'Nora Well',
      meta: '142k+ followers',
    },
    right: {
      image: require('../../images/pic2.webp'),
      name: 'Jasmine Patel',
      meta: '820k+ followers',
    },
  },
];

export default function AltNicheScrollerSection() {
  const stageWrapRef = useRef(null);
  const activeIndexRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);
  const touchStartYRef = useRef(null);
  const lockScrollYRef = useRef(0);
  const isLockedRef = useRef(false);
  const lockBlockedUntilExitRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const scrollDirectionRef = useRef(1);
  const observerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [transitionKey, setTransitionKey] = useState(0);
  const THRESHOLD = 120;

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const stageNode = stageWrapRef.current;
    if (!stageNode) return undefined;

    const maxIndex = NICHES.length - 1;

    const isMostlyInView = () => {
      const rect = stageNode.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const visible = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const ratio = Math.max(0, visible) / Math.max(1, rect.height);
      return ratio >= 0.95;
    };

    const lockScroll = (entryDirection) => {
      if (isLockedRef.current || lockBlockedUntilExitRef.current) return;
      isLockedRef.current = true;
      accumulatedDeltaRef.current = 0;
      touchStartYRef.current = null;
      lockScrollYRef.current = window.scrollY || window.pageYOffset || 0;
      document.body.style.overflow = 'hidden';
      setActiveIndex(entryDirection < 0 ? maxIndex : 0);
    };

    const unlockScroll = () => {
      if (!isLockedRef.current) return;
      isLockedRef.current = false;
      accumulatedDeltaRef.current = 0;
      touchStartYRef.current = null;
      document.body.style.overflow = '';
      window.scrollTo(0, lockScrollYRef.current);
    };

    const applyStep = (direction) => {
      setActiveIndex((prev) => {
        const next = Math.min(maxIndex, Math.max(0, prev + direction));
        if (next !== prev) setTransitionKey((k) => k + 1);
        return next;
      });
    };

    const releaseAtBoundary = (direction) => {
      lockBlockedUntilExitRef.current = true;
      unlockScroll();
      // Nudge enough to move out of the lock threshold zone.
      const releaseTargetY = lockScrollYRef.current + direction * 56;
      window.scrollTo(0, releaseTargetY);
      lastScrollYRef.current = releaseTargetY;
    };

    const consumeDelta = (delta) => {
      if (!isLockedRef.current || delta === 0) return;

      accumulatedDeltaRef.current += delta;

      while (accumulatedDeltaRef.current >= THRESHOLD) {
        if (activeIndexRef.current >= maxIndex) {
          releaseAtBoundary(1);
          return;
        }
        applyStep(1);
        accumulatedDeltaRef.current -= THRESHOLD;
      }

      while (accumulatedDeltaRef.current <= -THRESHOLD) {
        if (activeIndexRef.current <= 0) {
          releaseAtBoundary(-1);
          return;
        }
        applyStep(-1);
        accumulatedDeltaRef.current += THRESHOLD;
      }
    };

    const onWheel = (event) => {
      if (!isLockedRef.current) return;
      event.preventDefault();
      consumeDelta(event.deltaY);
    };

    const onTouchStart = (event) => {
      if (!isLockedRef.current || event.touches.length === 0) return;
      touchStartYRef.current = event.touches[0].clientY;
    };

    const onTouchMove = (event) => {
      if (!isLockedRef.current || event.touches.length === 0) return;
      const currentY = event.touches[0].clientY;
      const startY = touchStartYRef.current;
      if (startY == null) {
        touchStartYRef.current = currentY;
        return;
      }
      event.preventDefault();
      const deltaY = startY - currentY;
      touchStartYRef.current = currentY;
      consumeDelta(deltaY);
    };

    const onKeyDown = (event) => {
      if (!isLockedRef.current) return;
      let delta = 0;
      if (event.key === 'ArrowDown' || event.key === 'PageDown') delta = THRESHOLD;
      if (event.key === 'ArrowUp' || event.key === 'PageUp') delta = -THRESHOLD;
      if (event.key === ' ') delta = event.shiftKey ? -THRESHOLD : THRESHOLD;
      if (delta === 0) return;
      event.preventDefault();
      consumeDelta(delta);
    };

    const onWindowScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      scrollDirectionRef.current = y >= lastScrollYRef.current ? 1 : -1;
      lastScrollYRef.current = y;

      // Allow future lock only after user has actually moved away from the lock zone.
      if (lockBlockedUntilExitRef.current && !isMostlyInView()) {
        lockBlockedUntilExitRef.current = false;
      }
    };

    const onResize = () => {
      if (isLockedRef.current && !isMostlyInView()) {
        unlockScroll();
      }
    };

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.intersectionRatio >= 0.95) {
          lockScroll(scrollDirectionRef.current);
        } else if (isLockedRef.current) {
          unlockScroll();
        }
      },
      { threshold: [0, 0.95, 1] }
    );

    observerRef.current.observe(stageNode);

    lastScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKeyDown);

    return () => {
      unlockScroll();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      window.removeEventListener('scroll', onWindowScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const current = NICHES[activeIndex];
  const leftShift = activeIndex * 14;
  const rightShift = activeIndex * -14;

  return (
    <section className="alt-niche-scroll">

      <div className="alt-niche-scroll__intro alt-container">
        <h2 className="alt-niche-scroll__intro-title">
          Built for every niche.
          <br />
          Trusted by icons.
        </h2>
        <p className="alt-niche-scroll__intro-subtitle">
          Join <span>200,000+ creators</span> scaling
          <br />
          their success with Premsi.
        </p>
        <a href="/invitation" className="alt-niche-scroll__intro-cta">Join the movement</a>
      </div>

      {/*<div className="alt-niche-scroll__stage-wrap" ref={stageWrapRef}>
        <div className="alt-niche-scroll__stage alt-container">
          <article className="alt-niche-scroll__card alt-niche-scroll__card--left" style={{ transform: `translateY(${leftShift}px)` }}>
            <div key={`left-${activeIndex}-${transitionKey}`} className="alt-niche-scroll__card-inner">
              <img src={current.left.image} alt={current.left.name} loading="lazy" />
              <div className="alt-niche-scroll__card-meta">
                <strong>{current.left.name}</strong>
                <span>{current.left.meta}</span>
              </div>
            </div>
          </article>

          <ul className="alt-niche-scroll__labels" aria-label="Niches">
            {NICHES.map((item, index) => (
              <li
                key={item.label}
                className={`alt-niche-scroll__label${index === activeIndex ? ' alt-niche-scroll__label--active' : ''}`}
              >
                {item.label}
              </li>
            ))}
          </ul>

          <article className="alt-niche-scroll__card alt-niche-scroll__card--right" style={{ transform: `translateY(${rightShift}px)` }}>
            <div key={`right-${activeIndex}-${transitionKey}`} className="alt-niche-scroll__card-inner">
              <img src={current.right.image} alt={current.right.name} loading="lazy" />
              <div className="alt-niche-scroll__card-meta">
                <strong>{current.right.name}</strong>
                <span>{current.right.meta}</span>
              </div>
            </div>
          </article>
        </div>
      </div>*/}
      
    </section>
  );
}
